import React, { useState } from 'react';
import {
  Modal, Form, Button, Nav,
} from 'react-bootstrap';
import _ from 'lodash';
import ErrorAlert from '../../../Components/Error';
import OperatorService from '../../../Services/OperatorService';
import setStateFromInput from '../../../Utils/setStateFromInput';
import { CompanyFields, BillingFields, SettingsFields } from './OperatorFields';
import AddSettlementCurrencyModal from './AddSettlementCurrencyModal';
import { otherCurrency, settlementCurrencyPlan, companyName } from '../operatorGroups';
import {
  BILLING_FIELDS,
  COMPANY_SUMMARY,
  OPERATOR_FLAGS,
  defaultOperator,
  flagText,
  hasBillingDetails,
  isCreatable,
  justCreated,
  missingRequired,
  operatorChanges,
  operatorPayload,
  sectionFill,
  sectionFillLabel,
  stepIndexOf,
  stepsFor,
} from '../operatorWizard';

/**
 * An operator — adding one, or changing one that already exists.
 *
 * Four sections on both, and the one rule that keeps either from being
 * slower than the single column it replaced: **the save is live on every
 * one of them.** Creating, only the name and the settlement currency are
 * required, so the common operator — no billing, untouched settings — is
 * two boxes and one button on the first screen. Editing, the record already
 * satisfies everything the API asks for, so there is nothing a save could
 * be waiting on at all.
 *
 * Everything typed lives in ONE `operator` object for the modal's whole
 * life, so moving between sections cannot lose it — there is nothing to
 * lose it to. `opened` is that same object as it was on the first render,
 * kept untouched, and it is what the changes list is measured against.
 *
 * The rail is unlocked in both directions because nothing later depends on
 * anything earlier. On the edit path it is also stripped of everything that
 * would imply an order — no numbers, no "optional", no Back, no Next — and
 * carries instead how full each section is, so a blank one reads as blank
 * from wherever the user happens to be standing.
 *
 * When a CREATE lands, the last thing offered is the second settlement
 * currency, because the person who has just told us who this company is is
 * also the person who knows whether it is paid in both. That offer is
 * `AddSettlementCurrencyModal` itself, not a copy of it: adopting an
 * existing unpaired record rather than minting a third is the whole
 * difficulty, and it is already solved in there. An EDIT never reaches it —
 * see `handleSubmit`.
 */

function SummaryRow({ label, value }) {
  return (
    <div className="d-flex justify-content-between border-bottom py-1">
      <span className="text-muted me-3">{label}</span>
      <span className="text-end">{value || <em className="text-muted">Not set</em>}</span>
    </div>
  );
}

function SummarySection({ title, onEdit, children }) {
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <strong>{title}</strong>
        <Button variant="link" size="sm" className="p-0" onClick={onEdit}>
          Change
        </Button>
      </div>
      {children}
    </div>
  );
}

/** One altered field, as `was → will be`. */
function ChangeRow({ label, from, to }) {
  return (
    <div className="d-flex justify-content-between border-bottom py-1">
      <span className="text-muted me-3">{label}</span>
      <span className="text-end">
        <s className="text-muted">{from || <em>Not set</em>}</s>
        {' → '}
        <strong>{to || <em>Not set</em>}</strong>
      </span>
    </div>
  );
}

function OperatorWizard({
  originalOperator = {}, show = false, handleClose, fetchOperators,
}) {
  const isNewOperator = !originalOperator.Id;
  const steps = stepsFor(isNewOperator);

  // `_.defaults` writes into its first argument, and that argument used to be
  // the collection's own row — an empty object goes first so the screen's
  // state is never quietly stamped onto it.
  const [opened] = useState(
    () => _.defaults({}, originalOperator, defaultOperator(isNewOperator)),
  );
  const [operator, setOperator] = useState(opened);
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  // Set only once the operator is on the server, and only by a CREATE.
  // `created` is the record as the collection came back with it — the POST
  // answers with nothing, so it is read back rather than assumed — and
  // `rows` is that same collection, which is what says whether the other
  // currency is already on file.
  const [created, setCreated] = useState(null);
  const [rows, setRows] = useState([]);
  const [addingCurrency, setAddingCurrency] = useState(false);

  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const missing = missingRequired(operator);
  // Creating, the two required fields gate it. Editing, they are already
  // answered by the record on file, so the only thing a save waits for is
  // the last one finishing.
  const canSave = isNewOperator ? (isCreatable(operator) && !loading) : !loading;
  const changes = isNewOperator ? [] : operatorChanges(opened, operator);
  const emptySection = !isNewOperator && sectionFill(operator, step.key)?.filled === 0;

  const handleChange = (e) => setStateFromInput(e, setOperator, operator);

  const goTo = (key) => setStepIndex(stepIndexOf(steps, key));

  /** What the rail says about a section before anybody opens it. */
  const railLabel = (item, index) => {
    if (isNewOperator) {
      return `${index + 1}. ${item.title}${item.optional ? ' (optional)' : ''}`;
    }

    if (item.key === 'review') {
      return `${item.title} · ${changes.length || 'none'}`;
    }

    return `${item.title} · ${sectionFillLabel(operator, item.key)}`;
  };

  /**
   * The offer to make once the record exists, or nothing.
   *
   * Nothing is the right answer more often than it looks: a name that two
   * records now share cannot be resolved to the one just created, and an
   * operator whose company already carries both currencies has nothing to
   * add. Neither is an error — the operator WAS created — so neither is
   * ever reported as one.
   */
  const offerAfterCreate = async (sent) => {
    const fetched = await OperatorService.fetchOperators();
    const collection = fetched?.data?.data || [];
    const record = justCreated(collection, sent);

    if (!record) return false;
    if (settlementCurrencyPlan(record, collection).blocked) return false;

    setCreated(record);
    setRows(collection);
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setValidationErrors([]);

    const sent = operatorPayload(operator);

    try {
      await OperatorService.saveOperator(sent);
    } catch (err) {
      setError(err.response?.data?.message);

      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }

      setLoading(false);
      return;
    }

    /**
     * AN EDIT ENDS HERE, and that is a decision rather than an omission.
     *
     * The second-currency offer below is not merely unnecessary on an
     * existing record — it would FIRE, and wrongly. `justCreated` finds a
     * record in the collection by name and settlement currency, so on an
     * edit it hands back the very row being edited; `settlementCurrencyPlan`
     * only refuses a company that already settles in both or is already
     * paired, which the ordinary single-currency operator is not. Wired in,
     * every phone-number change to an unpaired operator would end with
     * "does this company also settle in HUF?".
     *
     * So the edit path does not read the collection back at all. It saves,
     * refreshes the list behind it, and closes — exactly what it did as a
     * flat form. Adding a settlement currency to an operator that exists is
     * already its own action on the operators list.
     */
    if (!isNewOperator) {
      try {
        await fetchOperators();
      } catch {
        // The list refreshing is the screen's problem, not the save's: the
        // record is already changed on the server.
      }

      setLoading(false);
      handleClose();
      return;
    }

    // Past here the operator exists. Nothing below may fail loudly.
    try {
      await fetchOperators();
    } catch {
      // The list refreshing is the screen's problem, not the create's.
    }

    let offering = false;

    try {
      offering = await offerAfterCreate(sent);
    } catch {
      offering = false;
    }

    setLoading(false);

    if (!offering) handleClose();
  };

  if (created && addingCurrency) {
    return (
      <AddSettlementCurrencyModal
        operator={created}
        operators={rows}
        show={show}
        fetchOperators={fetchOperators}
        handleClose={handleClose}
      />
    );
  }

  if (created) {
    const target = otherCurrency(created.settlement_currency);

    return (
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{`${created.name} is set up`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {`Does ${companyName(created.name)} also settle in ${target}?`}
          </p>
          <p className="text-muted small mb-0">
            {'A company paid in both currencies is kept as two operators, one per '
              + 'currency, linked so a single portal login answers for both. It can be '
              + 'added later from the operators list — but it is easier to say now.'}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {`No, ${created.settlement_currency} only`}
          </Button>
          <Button onClick={() => setAddingCurrency(true)}>
            {`Yes, add ${target}`}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isNewOperator ? 'New operator' : `Edit operator ${operator.name ?? ''}`.trim()}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorAlert error={error} setError={setError} />

        {/* Unlocked in both directions on purpose: these are sections, not
            gates, and a save that is already possible must never be behind
            a click on "Next". Editing goes further and drops the numbering
            too — every section is one click from every other, and nothing
            on screen should suggest there is an order to them. */}
        <Nav variant="tabs" className="mb-3" activeKey={step.key}>
          {steps.map((item, index) => (
            <Nav.Item key={item.key}>
              <Nav.Link
                as="button"
                type="button"
                eventKey={item.key}
                onClick={() => setStepIndex(index)}
              >
                {railLabel(item, index)}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <p className="text-muted small mb-1" aria-live="polite">
          {isNewOperator
            ? `Step ${stepIndex + 1} of ${steps.length} — `
              + `${step.title}${step.optional ? ' · optional' : ''}`
            : `${step.title} — open any section in any order; `
              + 'Save changes works from all of them.'}
        </p>
        <p className="mb-3">{step.lead}</p>

        <Form>
          {/* A section with nothing in it says so, in the section as well as
              on the rail. A row of blank boxes is what an unloaded screen
              looks like too, and "we have never invoiced this operator" is
              a fact worth not confusing with one. */}
          {emptySection && (
            <p className="fst-italic text-muted mb-3">
              {`Nothing is set in ${step.title} — every box below is empty.`}
            </p>
          )}

          {step.key === 'company' && (
            <CompanyFields
              operator={operator}
              onChange={handleChange}
              validationErrors={validationErrors}
            />
          )}

          {step.key === 'billing' && (
            <BillingFields
              operator={operator}
              onChange={handleChange}
              validationErrors={validationErrors}
            />
          )}

          {step.key === 'settings' && (
            <SettingsFields operator={operator} onChange={handleChange} />
          )}

          {step.key === 'review' && isNewOperator && (
            <>
              <SummarySection title="The company" onEdit={() => goTo('company')}>
                {/* The type row is never "Not set": the field always has a
                    value, and the one it defaults to is the one that matters
                    most to get right, so the review states it in full rather
                    than letting a skipped choice read as an absent one. */}
                {COMPANY_SUMMARY.map(({ key, label, read }) => (
                  <SummaryRow label={label} value={read(operator)} key={key} />
                ))}
              </SummarySection>

              <SummarySection title="Billing" onEdit={() => goTo('billing')}>
                {hasBillingDetails(operator) ? BILLING_FIELDS.map(({ key, label }) => (
                  <SummaryRow label={label} value={operator.billing?.[key]} key={key} />
                )) : (
                  <p className="text-muted small mb-0">
                    No billing details — which is normal, and can be filled in
                    whenever this operator first invoices us.
                  </p>
                )}
              </SummarySection>

              <SummarySection title="Settings" onEdit={() => goTo('settings')}>
                {OPERATOR_FLAGS.map((flag) => (
                  <SummaryRow
                    label={flag.label}
                    value={flagText(flag, operator[flag.key])}
                    key={flag.key}
                  />
                ))}
              </SummarySection>
            </>
          )}

          {step.key === 'review' && !isNewOperator && (
            changes.length === 0 ? (
              <p className="mb-0">
                Nothing has been changed. Saving now would send this operator
                back exactly as it is on file.
              </p>
            ) : (
              <>
                <p className="text-muted small">
                  {`${changes.length} ${changes.length === 1 ? 'field' : 'fields'} will change. `
                    + 'Everything else is sent back exactly as it was.'}
                </p>
                {changes.map(({ label, from, to }) => (
                  <ChangeRow label={label} from={from} to={to} key={label} />
                ))}
              </>
            )
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div>
          {/* Back and Next are create's: they are how somebody who does not
              yet know what is being asked for walks the sections once.
              Nothing on the edit path may imply a sequence. */}
          {isNewOperator && stepIndex > 0 && (
            <Button
              variant="link"
              onClick={() => setStepIndex(stepIndex - 1)}
            >
              Back
            </Button>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {isNewOperator && !canSave && !loading && (
            <span className="text-muted small">
              {`${missing.join(' and ')} needed to create`}
            </span>
          )}
          {isNewOperator && !isLastStep && (
            <Button
              variant="outline-primary"
              onClick={() => setStepIndex(stepIndex + 1)}
            >
              {step.key === 'billing' && !hasBillingDetails(operator)
                ? 'Skip billing'
                : `Next: ${steps[stepIndex + 1].title}`}
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={!canSave}>
            {isNewOperator ? 'Create operator' : 'Save changes'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default OperatorWizard;
